import { recipes, normalizePattern, CRAFT_GRID, type RecipeDef, type Station } from '../data/recipes';
import { itemDef } from '../data/items';
import type { Inventory } from '../player/Inventory';

/**
 * Üretim sistemi.
 * 8×8 ızgarada şekilli tarifler ızgaranın herhangi bir yerinde eşleşir.
 * Yanlış yerleşim ASLA üretim vermez; doğru tarif bulunursa sonuç önizlenir.
 */

export interface GridCell {
  item: string;
  count: number;
}

export type CraftGrid = (GridCell | null)[];

export const GRID_CELLS = CRAFT_GRID * CRAFT_GRID;

export function emptyGrid(): CraftGrid {
  return new Array(GRID_CELLS).fill(null);
}

/** Izgaranın dolu hücrelerinin sınırlayıcı kutusu. */
function gridBounds(grid: CraftGrid): { minR: number; maxR: number; minC: number; maxC: number; count: number } {
  let minR = Infinity;
  let maxR = -Infinity;
  let minC = Infinity;
  let maxC = -Infinity;
  let count = 0;
  for (let i = 0; i < GRID_CELLS; i++) {
    if (!grid[i]) continue;
    const r = Math.floor(i / CRAFT_GRID);
    const c = i % CRAFT_GRID;
    count++;
    if (r < minR) minR = r;
    if (r > maxR) maxR = r;
    if (c < minC) minC = c;
    if (c > maxC) maxC = c;
  }
  return { minR, maxR, minC, maxC, count };
}

function matchShaped(recipe: RecipeDef, grid: CraftGrid): boolean {
  if (!recipe.pattern || !recipe.key) return false;
  const norm = normalizePattern(recipe.pattern);
  if (norm.w === 0) return false;
  const b = gridBounds(grid);
  if (b.count === 0) return false;
  if (b.maxR - b.minR + 1 !== norm.h || b.maxC - b.minC + 1 !== norm.w) return false;

  for (let r = 0; r < norm.h; r++) {
    for (let c = 0; c < norm.w; c++) {
      const ch = norm.cells[r]![c];
      const cell = grid[(b.minR + r) * CRAFT_GRID + (b.minC + c)] ?? null;
      if (ch === null) {
        if (cell) return false;
      } else {
        const need = recipe.key[ch];
        if (!need || !cell || cell.item !== need) return false;
      }
    }
  }
  return true;
}

function matchShapeless(recipe: RecipeDef, grid: CraftGrid): boolean {
  if (!recipe.ingredients) return false;
  const have = new Map<string, number>();
  for (const cell of grid) {
    if (!cell) continue;
    have.set(cell.item, (have.get(cell.item) ?? 0) + cell.count);
  }
  if (have.size !== new Set(recipe.ingredients.map((i) => i.item)).size) return false;
  for (const ing of recipe.ingredients) {
    if ((have.get(ing.item) ?? 0) !== ing.count) return false;
  }
  return true;
}

/** Izgaraya uyan tarifi bulur. */
export function findRecipeForGrid(grid: CraftGrid, station: Station): RecipeDef | null {
  const allowed: Station[] = station === 'workbench' ? ['hand', 'workbench'] : [station];
  for (const r of recipes.all()) {
    if (!allowed.includes(r.station)) continue;
    if (r.pattern ? matchShaped(r, grid) : matchShapeless(r, grid)) return r;
  }
  return null;
}

/** Envanterden yapılabilecek tarifler (liste görünümü için). */
export function availableRecipes(inv: Inventory, station: Station): Array<{ recipe: RecipeDef; craftable: boolean; missing: string[] }> {
  const allowed: Station[] = station === 'workbench' ? ['hand', 'workbench'] : [station];
  const out: Array<{ recipe: RecipeDef; craftable: boolean; missing: string[] }> = [];
  for (const r of recipes.all()) {
    if (!allowed.includes(r.station)) continue;
    const need = requirementsOf(r);
    const missing: string[] = [];
    for (const [item, count] of need) {
      if (inv.count(item) < count) missing.push(`${itemDef(item).name} x${count - inv.count(item)}`);
    }
    out.push({ recipe: r, craftable: missing.length === 0, missing });
  }
  // Yapılabilirler önce, sonra kategori/isim
  out.sort((a, b) => Number(b.craftable) - Number(a.craftable) || (a.recipe.category ?? '').localeCompare(b.recipe.category ?? ''));
  return out;
}

/** Tarifin toplam malzeme ihtiyacı (desen veya şekilsiz). */
export function requirementsOf(recipe: RecipeDef): Map<string, number> {
  const need = new Map<string, number>();
  if (recipe.ingredients) {
    for (const ing of recipe.ingredients) need.set(ing.item, (need.get(ing.item) ?? 0) + ing.count);
  } else if (recipe.pattern && recipe.key) {
    for (const row of recipe.pattern) {
      for (const ch of row) {
        if (ch === ' ') continue;
        const id = recipe.key[ch];
        if (id) need.set(id, (need.get(id) ?? 0) + 1);
      }
    }
  }
  return need;
}

export type CraftOutcome = { ok: true; item: string; count: number } | { ok: false; reason: string };
export type RepairOutcome = { ok: true; item: string } | { ok: false; reason: string };

/** Hasarlı araç/zırhı çalışma masasında aynı malzemeden bir parça ile tamir eder. */
export function repairEquipment(inv: Inventory, index: number): RepairOutcome {
  const slot = inv.slots[index];
  const def = slot ? itemDef(slot.item) : null;
  const max = def?.tool?.durability ?? def?.armor?.durability;
  if (!slot || !def || max === undefined || slot.dur === undefined) return { ok: false, reason: 'Tamir edilecek eşya yok' };
  if (slot.dur >= max && !slot.broken) return { ok: false, reason: 'Eşya zaten sağlam' };
  const material = def.material ?? (def.tool?.class === 'bow' || def.tool?.class === 'rod' ? 'wood' : null);
  if (!material || !inv.has(material, 1)) return { ok: false, reason: `Yetersiz: ${material ? itemDef(material).name : 'malzeme'}` };
  if (!inv.remove(material, 1)) return { ok: false, reason: 'Malzeme düşülemedi' };
  if (!inv.repairAt(index)) {
    inv.add(material, 1);
    return { ok: false, reason: 'Tamir edilemedi' };
  }
  return { ok: true, item: def.id };
}

/** Listeden üretim: malzemeler envanterden düşülür. */
export function craftFromInventory(inv: Inventory, recipe: RecipeDef, times = 1): CraftOutcome {
  const need = requirementsOf(recipe);
  for (const [item, count] of need) {
    if (inv.count(item) < count * times) return { ok: false, reason: `Yetersiz: ${itemDef(item).name}` };
  }
  const out = recipe.output.count * times;
  // Çıktı için yer var mı? (araçlar yığınlanmaz)
  const stack = itemDef(recipe.output.item).stack;
  if (stack === 1 && inv.freeSlots() < out) return { ok: false, reason: 'Çantada yer yok' };

  for (const [item, count] of need) {
    if (!inv.remove(item, count * times)) return { ok: false, reason: 'Malzeme düşülemedi' };
  }
  const left = inv.add(recipe.output.item, out);
  if (left > 0) {
    // Yer kalmadıysa kalanı geri veremeyiz; malzemeyi iade et
    for (const [item, count] of need) inv.add(item, count * times);
    inv.remove(recipe.output.item, out - left);
    return { ok: false, reason: 'Çantada yer yok' };
  }
  return { ok: true, item: recipe.output.item, count: out };
}

/** Izgaradan üretim: ızgaradaki eşyalar tüketilir. */
export function craftFromGrid(inv: Inventory, grid: CraftGrid, station: Station): CraftOutcome {
  const recipe = findRecipeForGrid(grid, station);
  if (!recipe) return { ok: false, reason: 'Geçerli tarif yok' };
  const left = inv.add(recipe.output.item, recipe.output.count);
  if (left > 0) {
    inv.remove(recipe.output.item, recipe.output.count - left);
    return { ok: false, reason: 'Çantada yer yok' };
  }
  for (let i = 0; i < grid.length; i++) grid[i] = null;
  return { ok: true, item: recipe.output.item, count: recipe.output.count };
}
